import type { AdminRecord } from '@/types/record'
import type { ColumnDef, FilterDef, PageDefinition, SelectOption, SummaryDef } from '@/types/page'
import type { ListQuery } from '@/types/page'
import { formatDate, formatDateTime, safeText } from '@/utils/format'

export interface QueryResult {
  rows: AdminRecord[]
  total: number
  page: number
  pageSize: number
  summary: Record<string, number>
}

function textOf(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.map(item => textOf(item)).join(' ')
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).map(item => textOf(item)).join(' ')
  return String(value)
}

function equalsFilter(record: AdminRecord, key: string, value: unknown): boolean {
  if (value === '' || value === undefined || value === null) return true
  if (Array.isArray(value)) {
    if (!value.length) return true
    return value.some(item => equalsFilter(record, key, item))
  }
  const current = record[key]
  if (Array.isArray(current)) return current.map(String).includes(String(value))
  return String(current ?? '') === String(value)
}

function matchesWhere(record: AdminRecord, where: Record<string, string | boolean | number>): boolean {
  return Object.entries(where).every(([key, value]) => {
    if (key === 'age_gte') return Number(record.age ?? 0) >= Number(value)
    if (key === 'battery_lte') return Number(record.battery ?? 0) <= Number(value)
    if (key === 'registered_within_days') return formatDate(record.registered_at) >= formatDate(Date.now() - Number(value) * 86_400_000)
    if (key === 'created_within_days') return formatDate(record.created_at) >= formatDate(Date.now() - Number(value) * 86_400_000)
    if (key === 'stock_low') {
      return Number(record.stock ?? 0) < Number(record.safety_stock ?? 0)
    }
    if (key.endsWith('_gte')) {
      const field = key.slice(0, -4)
      return Number(record[field] ?? 0) >= Number(value)
    }
    if (key.endsWith('_lte')) {
      const field = key.slice(0, -4)
      return Number(record[field] ?? 0) <= Number(value)
    }
    if (typeof value === 'boolean') {
      return Boolean(record[key]) === value
    }
    return String(record[key] ?? '') === String(value)
  })
}

function specialFilter(record: AdminRecord, key: string, value: string): boolean | undefined {
  if (key === 'role_code') {
    return Array.isArray(record.role_codes) && (record.role_codes as unknown[]).map(String).includes(value)
  }
  if (key === 'bound') {
    const unbound = record.status === 'unbound'
    return value === 'true' ? !unbound : unbound
  }
  if (key === 'device_bound') {
    return value === 'true' ? Boolean(record.device_bound) : !record.device_bound
  }
  if (key === 'stock_state') {
    const stock = Number(record.stock ?? 0)
    const safety = Number(record.safety_stock ?? 0)
    if (value === 'out') return stock <= 0
    if (value === 'low') return stock > 0 && stock < safety
    if (value === 'sufficient') return stock >= safety
    return true
  }
  if (key === 'age_group') {
    const age = Number(record.age ?? 0)
    if (value === 'age_under_45') return age < 45
    if (value === 'age_45_59') return age >= 45 && age <= 59
    if (value === 'age_60_74') return age >= 60 && age <= 74
    if (value === 'age_75_plus') return age >= 75
    return true
  }
  if (key === 'plan_name') {
    return textOf(record.plan_name) === value || textOf(record.name) === value
  }
  if (key === 'drug_name' || key === 'category_name' || key === 'folder') {
    return String(record[key] ?? '') === value
  }
  if (key === 'channel') {
    return Array.isArray(record.channels) && record.channels.map(String).includes(value)
  }
  if (key === 'period_months') {
    return String(record.period_months ?? '') === value
  }
  return undefined
}

function withinDateRange(record: AdminRecord, field: string, range: unknown): boolean {
  if (!Array.isArray(range) || range.length !== 2 || (!range[0] && !range[1])) return true
  const raw = record[field]
  if (raw === null || raw === undefined || raw === '') return false
  const time = new Date(String(raw).length === 10 ? `${String(raw)}T00:00:00+08:00` : String(raw)).getTime()
  if (!Number.isFinite(time)) return false
  const start = range[0] ? new Date(`${String(range[0])}T00:00:00+08:00`).getTime() : null
  const end = range[1] ? new Date(`${String(range[1])}T23:59:59+08:00`).getTime() : null
  if (start !== null && time < start) return false
  if (end !== null && time > end) return false
  return true
}

export function matchesFilter(record: AdminRecord, filter: FilterDef, value: unknown): boolean {
  if (value === '' || value === undefined || value === null) return true
  if (Array.isArray(value) && value.length === 0) return true
  if (filter.type === 'keyword' || filter.type === 'daterange') return true
  const key = filter.field ?? filter.key
  const text = String(value)
  const special = specialFilter(record, key, text)
  if (special !== undefined) return special
  return equalsFilter(record, key, value)
}

export function filterRecords(records: AdminRecord[], def: PageDefinition, query: ListQuery): AdminRecord[] {
  const keyword = query.keyword.trim().toLowerCase()
  const statusKey = def.statusKey ?? 'status'
  const timeField = query.filters?.__timeField as string | undefined

  return records.filter(record => {
    if (query.scopeRegionId && typeof record.region_id === 'string' && record.region_id !== query.scopeRegionId) {
      return false
    }
    if (keyword) {
      const haystack = (def.searchKeys.length ? def.searchKeys : Object.keys(record))
        .map(key => textOf(record[key]))
        .join(' ')
        .toLowerCase()
      if (!haystack.includes(keyword)) return false
    }
    if (query.status && String(record[statusKey] ?? '') !== query.status) return false
    for (const [key, value] of Object.entries(query.filters ?? {})) {
      if (key === '__timeField') continue
      const filter = def.filters.find(item => item.key === key)
      if (!filter) continue
      if (filter.type === 'daterange') {
        const field = filter.timeField ?? def.timeField ?? 'created_at'
        if (!withinDateRange(record, field, value)) return false
        continue
      }
      if (!matchesFilter(record, filter, value)) return false
    }
    if (query.dateStart || query.dateEnd) {
      const field = def.filters.find(filter => filter.type === 'daterange')?.timeField ?? def.timeField ?? 'created_at'
      const day = formatDate(record[field])
      const start = query.dateStart ? formatDate(query.dateStart) : ''
      const end = query.dateEnd ? formatDate(query.dateEnd) : ''
      if (start && day < start) return false
      if (end && day > end) return false
      if (timeField && timeField !== field && record[timeField]) {
        const alt = formatDate(record[timeField])
        if (start && alt < start) return false
        if (end && alt > end) return false
      }
    }
    return true
  })
}

function valueForSummary(record: AdminRecord, def: SummaryDef): number | string | undefined {
  return record[def.field ?? '']
}

export function summarize(records: AdminRecord[], def: PageDefinition): Record<string, number> {
  const summary: Record<string, number> = {}
  for (const item of def.summary ?? []) {
    const scoped = item.where ? records.filter(record => matchesWhere(record, item.where ?? {})) : records
    if (item.kind === 'count') {
      summary[item.key] = scoped.length
    } else if (item.kind === 'sum' || item.kind === 'money') {
      summary[item.key] = scoped.reduce((total, record) => {
        const value = Number(valueForSummary(record, item) ?? 0)
        return total + (Number.isFinite(value) ? value : 0)
      }, 0)
    } else if (item.kind === 'distinct') {
      const values = new Set<string>()
      for (const record of scoped) {
        const value = valueForSummary(record, item)
        if (Array.isArray(value)) value.forEach(entry => { if (entry) values.add(String(entry)) })
        else if (value !== '' && value !== null && value !== undefined) values.add(String(value))
      }
      summary[item.key] = values.size
    } else if (item.kind === 'ratio') {
      if (item.field) {
        if (!records.length) {
          summary[item.key] = 0
          continue
        }
        const total = records.reduce((sum, record) => sum + (Number(record[item.field as string] ?? 0) || 0), 0)
        summary[item.key] = Math.round((total / records.length) * 10) / 10
      } else {
        const denominator = item.denominatorWhere
          ? records.filter(record => matchesWhere(record, item.denominatorWhere ?? {})).length
          : records.length
        summary[item.key] = denominator ? Math.round((scoped.length / denominator) * 1000) / 10 : 0
      }
    }
  }
  return summary
}

function compareValues(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b
  const left = String(a ?? '')
  const right = String(b ?? '')
  return left.localeCompare(right, 'zh-CN', { numeric: true })
}

export function queryRecords(records: AdminRecord[], def: PageDefinition, query: ListQuery): QueryResult {
  const filtered = filterRecords(records, def, query)
  const sortField = query.sortField
  if (sortField) {
    filtered.sort((left, right) => {
      const result = compareValues(left[sortField], right[sortField])
      return query.sortOrder === 'desc' ? -result : result
    })
  } else {
    const natural = def.timeField ? 'updated_at' : 'created_at'
    filtered.sort((left, right) => compareValues(right[natural], left[natural]))
  }
  const pageSize = [10, 20, 50, 100].includes(query.pageSize) ? query.pageSize : 20
  const total = filtered.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const page = Math.min(Math.max(1, query.page || 1), totalPages)
  const start = (page - 1) * pageSize
  return {
    rows: filtered.slice(start, start + pageSize).map(record => ({ ...record })),
    total,
    page,
    pageSize,
    summary: summarize(filtered, def),
  }
}

export interface ColumnFormat {
  text: string
  tone?: string
  option?: SelectOption
}

export function columnValue(record: AdminRecord, column: ColumnDef): ColumnFormat {
  const value = record[column.key]
  const option = column.options?.find(item => String(item.value) === String(value))
  if (column.type === 'datetime') return { text: formatDateTime(value) }
  if (column.type === 'date') return { text: formatDate(value) }
  if (column.type === 'money') {
    const number = Number(value) || 0
    return { text: `¥${(number / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }
  }
  if (column.type === 'number') {
    const number = Number(value)
    return { text: `${Number.isFinite(number) ? number.toLocaleString('zh-CN') : '—'}${column.suffix ?? ''}` }
  }
  if (column.type === 'percent') {
    const number = Number(value)
    return { text: `${Number.isFinite(number) ? number.toFixed(1) : '0.0'}${column.suffix ?? '%'}` }
  }
  if (column.type === 'phone') {
    const text = String(value ?? '')
    return { text: /^1\d{10}$/.test(text) ? `${text.slice(0, 3)}****${text.slice(7)}` : safeText(value) }
  }
  if (column.type === 'boolean') return { text: value ? '是' : '否' }
  if (column.type === 'status') return { text: option?.label ?? safeText(value), tone: option?.tone, option }
  if (column.type === 'tag') {
    const values = Array.isArray(value) ? value : String(value ?? '').split(/[、,，]/).filter(Boolean)
    return { text: values.map(item => {
      const itemOption = column.options?.find(optionItem => String(optionItem.value) === String(item))
      return itemOption?.label ?? String(item)
    }).join('、') }
  }
  if (column.type === 'progress') return { text: `${Math.round(Number(value) || 0)}%` }
  if (option) return { text: option.label, tone: option.tone, option }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return { text: formatDateTime(value) }
  return { text: safeText(value) }
}
